import { useState, useEffect } from "react";
import { StudentBilling, updateStudentBilling } from "@/hooks/useFinanceData";
import { toast } from "sonner";

export const useOptimisticBilling = (originalBilling: StudentBilling[], refetchBilling: () => void) => {
  const [localBilling, setLocalBilling] = useState(originalBilling);
  const [loadingBillId, setLoadingBillId] = useState<string | null>(null);

  useEffect(() => {
    setLocalBilling(originalBilling);
  }, [originalBilling]);

  const handleMarkAsPaid = async (billId: string) => {
    try {
      setLoadingBillId(billId);
      
      // Actualización optimista - actualizar UI inmediatamente
      const currentBill = localBilling.find(b => b.id === billId);
      if (currentBill) {
        const updatedBilling = localBilling.map(bill => 
          bill.id === billId 
            ? { ...bill, status: 'paid', paid_date: new Date().toISOString().split('T')[0] }
            : bill
        );
        setLocalBilling(updatedBilling);
      }
      
      await updateStudentBilling(billId, {
        status: 'paid',
        paid_date: new Date().toISOString().split('T')[0]
      });
      
      toast.success("Factura marcada como pagada");
    } catch (error: any) {
      
      toast.error("Error al marcar como pagada: " + error.message);
      // Revertir cambio optimista en caso de error
      refetchBilling();
    } finally {
      setLoadingBillId(null);
    }
  };

  const handleMarkAsPending = async (billId: string) => {
    try {
      setLoadingBillId(billId);
      
      // Actualización optimista
      const currentBill = localBilling.find(b => b.id === billId);
      if (currentBill) {
        const updatedBilling = localBilling.map(bill => 
          bill.id === billId 
            ? { ...bill, status: 'pending', paid_date: null }
            : bill
        );
        setLocalBilling(updatedBilling);
      }
      
      await updateStudentBilling(billId, {
        status: 'pending',
        paid_date: null
      });
      
      toast.success("Factura marcada como pendiente");
    } catch (error: any) {
      
      toast.error("Error al marcar como pendiente: " + error.message);
      // Revertir cambio optimista en caso de error
      refetchBilling();
    } finally {
      setLoadingBillId(null);
    }
  };

  const handleMarkAllAsPaid = async () => {
    try {
      const pendingBills = localBilling.filter(bill => bill.status === 'pending');
      
      if (pendingBills.length === 0) {
        toast.error("No hay facturas pendientes");
        return;
      }

      setLoadingBillId('all');
      
      // Actualización optimista - marcar todas como pagadas
      const updatedBilling = localBilling.map(bill => 
        bill.status === 'pending' 
          ? { ...bill, status: 'paid', paid_date: new Date().toISOString().split('T')[0] }
          : bill
      );
      setLocalBilling(updatedBilling);
      
      // Actualizar todas las facturas pendientes en paralelo
      const updatePromises = pendingBills.map(bill => 
        updateStudentBilling(bill.id, {
          status: 'paid',
          paid_date: new Date().toISOString().split('T')[0]
        })
      );
      
      await Promise.all(updatePromises);
      
      toast.success(`${pendingBills.length} facturas marcadas como pagadas`);
    } catch (error: any) {
      
      toast.error("Error al marcar facturas como pagadas: " + error.message);
      // Revertir cambio optimista en caso de error
      refetchBilling();
    } finally {
      setLoadingBillId(null);
    }
  };

  return {
    billing: localBilling,
    loadingBillId,
    handleMarkAsPaid,
    handleMarkAsPending,
    handleMarkAllAsPaid
  };
};